import { PfileTypeId } from '@lib2/model/pfile/PFileType';

// ── Image Spec (UI metadata only -- enum IDs come from lib2) ──

/** Client-side verdict on a decoded candidate: block stops the upload, warn shows once and continues. */
export interface PfileImageValidation {
  block?: string;
  warn?: string;
}

export interface PfileImageSpec {
  pfileTypeId: PfileTypeId;
  deletePfileTypeIds: PfileTypeId[];
  label: string;
  recommendedSize: string;
  aspectRatio: number;
  maxFileSizeMb: number;
  /**
   * When set, uploads must match these pixel dimensions exactly (blocked
   * client-side before upload). Roku store/package assets are rejected at
   * certification when off-spec; web slots stay recommendation-only. With a
   * `preprocess` hook the check runs on the PROCESSED file.
   */
  exactSize?: { w: number; h: number };
  /**
   * Guidance shown under the label (jvm notes/biz/kb/kb_artwork_guide_v1.md
   * Part 1: two lines + one "Avoid" line, each under 90 characters).
   */
  guidance?: readonly string[];
  avoid?: string;
  /** Empty-state line for an optional image (what the app uses without it). */
  emptyStateHint?: string;
  /** What the uploader does to the file before upload (stated, never silent). */
  behaviorNote?: string;
  /** Per-spec picker restriction; defaults to every accepted image type. */
  acceptMimeTypes?: readonly string[];
  acceptExtensions?: readonly string[];
  /** Short label for the accepted types in the dropzone hint (default lists them all). */
  acceptLabel?: string;
  /**
   * Bitmap-level checks (dimensions, alpha, luminance) — the guide's Part 2,
   * mechanical and client-side so the message carries the exact numbers.
   */
  validate?: (file: File, bitmap: ImageBitmap) => PfileImageValidation | Promise<PfileImageValidation>;
  /** Transform before upload (fit + pad, re-encode); runs after `validate`. */
  preprocess?: (file: File, bitmap: ImageBitmap) => Promise<File>;
}

export const APP_WEB_IMAGE_SPECS: PfileImageSpec[] = [
  {
    pfileTypeId: PfileTypeId.AppWebOHLogo,
    deletePfileTypeIds: [PfileTypeId.AppWebOHLogo],
    label: 'Logo',
    recommendedSize: '300 x 100',
    aspectRatio: 3,
    maxFileSizeMb: 5,
  },
  {
    pfileTypeId: PfileTypeId.AppWebSiteImg,
    deletePfileTypeIds: [PfileTypeId.AppWebSiteImg],
    label: 'Site Image',
    recommendedSize: '1200 x 630',
    aspectRatio: 1200 / 630,
    maxFileSizeMb: 10,
  },
  {
    pfileTypeId: PfileTypeId.AppWebIcon192,
    deletePfileTypeIds: [PfileTypeId.AppWebIcon192],
    label: 'App Icon',
    recommendedSize: '512 x 512',
    aspectRatio: 1,
    maxFileSizeMb: 5,
  },
];


/**
 * The 16:9 poster of a series or category (design ADR-0016: drawn as uploaded
 * on the TV home screen — in rows and, when featured, large; a one-item app's
 * poster fills the whole screen under the Immersive and Full-bleed hero styles).
 */
export const CONTENT_IMAGE_SPECS: PfileImageSpec[] = [
  {
    pfileTypeId: PfileTypeId.ListPoster,
    deletePfileTypeIds: [PfileTypeId.ListPoster, PfileTypeId.ListThumb],
    label: 'Poster Image',
    recommendedSize: '1920 x 1080',
    aspectRatio: 16 / 9,
    maxFileSizeMb: 20,
    guidance: [
      "Shown in rows and, when featured, large on your TV app's home screen. 16:9, 1920 x 1080.",
      'A picture suits the Immersive and Full-bleed hero styles; text on the poster suits Card.',
    ],
    avoid: 'Avoid: small text, and anything important near the top or bottom edge.',
  },
];

// ── Accepted Image Types ──
export const PFILE_ACCEPTED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
] as const;

export const PFILE_ACCEPTED_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.heic',
  '.heif',
] as const;

export const PFILE_PROCESSING_TIMEOUT_MS = 60_000;
